import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import { supabase } from "../../lib/supabaseClient";
import { useAppSelector, useAppDispatch } from "../../hooks";
import { setUser, clearUser } from "../../features/auth/authSlice";
import UsernameModal from "../Modals/UsernameModal/UsernameModal";
import defaultUserAvatar from "../../assets/defaultuser-avatar.svg";
import googleSvg from "../../assets/google.svg";

const Header: React.FC = () => {
  const dispatch = useAppDispatch();
  const authUser = useAppSelector((s) => s.auth.user);
  const profileLoaded = useAppSelector((s) => s.auth.profileLoaded);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);

  useEffect(() => {
    // Only show modal if profile has been loaded AND user doesn't have username
    // This prevents the modal from flashing on page refresh while profile is loading
    if (authUser && profileLoaded && !authUser.username) {
      setShowUsernameModal(true);
    } else {
      setShowUsernameModal(false);
    }
  }, [authUser, profileLoaded]);

  const handleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/`,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });

      if (error) {
        // console.error("Sign in error:", error);
        alert("Failed to sign in. Please try again.");
      }
    } catch (err) {
      // console.error("Sign in error:", err);
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      dispatch(clearUser());
      setShowUserMenu(false);
    } catch (err) {
      // console.error("Sign out error:", err);
    }
  };

  const handleProfileComplete = async () => {
    setShowUsernameModal(false);
    if (authUser) {
      const { data: profile, error } = await supabase
        .from("user_profiles")
        .select("username, display_name, avatar_url")
        .eq("id", authUser.id)
        .single();

      if (error) {
        // console.error("Error refetching profile:", error);
      } else {
        dispatch(setUser({ ...authUser, ...profile }));
      }
    }
  };

  return (
    <>
      <header className="bg-gray-900 border-b-2 border-red-600 sticky top-0 z-50 shadow-lg">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="font-ds-700 text-white text-xl md:text-2xl lg:text-3xl">
              Noleyo
            </Link>

            {/* Auth Section */}
            {authUser ? (
              <div className="relative">
                {(() => {
                  const primaryAvatar = authUser.avatar_url && authUser.avatar_url.trim().length > 0 ? authUser.avatar_url : undefined;
                  const metadataAvatar =
                    authUser.user_metadata?.avatar_url && authUser.user_metadata.avatar_url.trim().length > 0
                      ? authUser.user_metadata.avatar_url
                      : undefined;
                  const avatarSrc = primaryAvatar || metadataAvatar || defaultUserAvatar;

                  return (
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 rounded-lg px-3 py-2 transition-colors"
                    >
                      <img
                        key={avatarSrc}
                        src={avatarSrc}
                        alt="Avatar"
                        className="w-8 h-8 rounded-full border-2 border-red-600"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = defaultUserAvatar;
                        }}
                      />
                      <span className="text-white font-semibold hidden sm:block">
                        {authUser.display_name || authUser.user_metadata?.name || "Anonymous"}
                      </span>
                      <svg
                        className={`w-4 h-4 text-gray-400 transition-transform ${showUserMenu ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  );
                })()}

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-gray-800 border-2 border-red-600 rounded-lg shadow-xl py-2 z-50">
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-white hover:bg-gray-700 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      My Profile
                    </Link>
                    <Link
                      to="/watch-parties"
                      className="block px-4 py-2 text-white hover:bg-gray-700 transition-colors"
                      onClick={() => setShowUserMenu(false)}
                    >
                      Watch Parties
                    </Link>
                    <hr className="border-gray-700 my-2" />
                    <button
                      onClick={handleSignOut}
                      className="block w-full text-left px-4 py-2 text-red-400 hover:bg-gray-700 transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleSignIn}
                className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
              >
                <img src={googleSvg} alt="Google" className="w-5 h-5" />
                <span>Sign in</span>
              </button>
            )}
          </div>
        </div>

        {showUserMenu && <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)}></div>}
      </header>

      {/* Username Selection Modal */}
      {showUsernameModal && authUser && (
        <UsernameModal
          googleName={authUser.user_metadata?.full_name || authUser.user_metadata?.name || "User"}
          userId={authUser.id}
          avatarUrl={authUser.user_metadata?.avatar_url}
          onComplete={handleProfileComplete}
        />
      )}
    </>
  );
};

export default Header;
